import type { CampaignResponse } from "@voice2mail/shared/v2m_api";
import { useCallback, useState } from "react";

import * as api from "../api/client";
import { CampaignProgress } from "../components/CampaignProgress";
import { CampaignWizard } from "../components/CampaignWizard";
import { QuotaStatus } from "../components/QuotaStatus";
import { SortableTh } from "../components/SortableTh";
import { useApi } from "../hooks/useApi";
import { useSortableRows } from "../hooks/useSortableRows";

type PageView = "list" | "wizard" | "progress";

type CampaignSortKey =
  | "template"
  | "status"
  | "totalRecipients"
  | "sentCount"
  | "failedCount"
  | "createdAt";
const ACCESSORS: Record<CampaignSortKey, (c: CampaignResponse) => string | number> = {
  template: (c) => c.template,
  status: (c) => c.status,
  totalRecipients: (c) => c.totalRecipients,
  sentCount: (c) => c.sentCount,
  failedCount: (c) => c.failedCount,
  createdAt: (c) => c.createdAt.getTime(),
};

export function CampaignsPage() {
  const [view, setView] = useState<PageView>("list");
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);

  const campaigns = useApi(() => api.listCampaigns(), [view]);
  // Default: most-recently-created campaign first — useful without clicking.
  const campaignSort = useSortableRows(campaigns.data, ACCESSORS, {
    key: "createdAt",
    direction: "desc",
  });

  const handleViewProgress = useCallback((campaignId: string) => {
    setSelectedCampaignId(campaignId);
    setView("progress");
  }, []);

  const handleDispatched = useCallback((campaignId: string) => {
    setSelectedCampaignId(campaignId);
    setView("progress");
  }, []);

  return (
    <div>
      {view === "list" && (
        <>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Email Campaigns</h2>
              <p className="text-sm text-slate-500 mt-1">
                Create, test, and dispatch bulk email campaigns.
              </p>
            </div>
            <button
              type="button"
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg transition-colors"
              onClick={() => setView("wizard")}
            >
              New Campaign
            </button>
          </div>

          <div className="mb-6">
            <QuotaStatus />
          </div>

          {campaigns.loading && (
            <div className="text-slate-500 text-sm py-6 text-center">Loading campaigns…</div>
          )}

          {!campaigns.loading && campaigns.error && (
            <div
              className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4"
              role="alert"
            >
              Error: {campaigns.error}
            </div>
          )}

          {!campaigns.loading && campaigns.data && campaigns.data.length === 0 && (
            <div className="text-slate-500 text-sm py-10 text-center border border-dashed border-slate-300 rounded-lg">
              No campaigns yet. Create your first email campaign.
            </div>
          )}

          {!campaigns.loading && campaigns.data && campaigns.data.length > 0 && (
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <SortableTh
                        label="Template"
                        sortKey="template"
                        sort={campaignSort.sort}
                        onSort={campaignSort.toggleSort}
                        sticky
                      />
                      <SortableTh
                        label="Status"
                        sortKey="status"
                        sort={campaignSort.sort}
                        onSort={campaignSort.toggleSort}
                      />
                      <SortableTh
                        label="Recipients"
                        sortKey="totalRecipients"
                        sort={campaignSort.sort}
                        onSort={campaignSort.toggleSort}
                      />
                      <SortableTh
                        label="Sent"
                        sortKey="sentCount"
                        sort={campaignSort.sort}
                        onSort={campaignSort.toggleSort}
                      />
                      <SortableTh
                        label="Failed"
                        sortKey="failedCount"
                        sort={campaignSort.sort}
                        onSort={campaignSort.toggleSort}
                      />
                      <SortableTh
                        label="Created"
                        sortKey="createdAt"
                        sort={campaignSort.sort}
                        onSort={campaignSort.toggleSort}
                      />
                      <th className="text-right px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {campaignSort.sorted.map((c) => (
                      <tr key={c.id} className="group hover:bg-slate-50 transition-colors">
                        <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 px-4 py-3 text-sm text-slate-900">
                          {c.template}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                            {c.status.replace("_", " ")}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-900">{c.totalRecipients}</td>
                        <td className="px-4 py-3 text-sm text-slate-900">{c.sentCount}</td>
                        <td className="px-4 py-3 text-sm text-slate-900">{c.failedCount}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {c.createdAt.toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          <button
                            type="button"
                            className="text-blue-600 hover:text-blue-800 font-medium transition-colors"
                            onClick={() => handleViewProgress(c.id)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {view === "wizard" && (
        <CampaignWizard onDispatched={handleDispatched} onCancel={() => setView("list")} />
      )}

      {view === "progress" && selectedCampaignId && (
        <CampaignProgress campaignId={selectedCampaignId} onBack={() => setView("list")} />
      )}
    </div>
  );
}
